/**
 * Minimal chunked uploader for the anomaly detection flow.
 * Streams a CSV file to /api/adjustmentsOfData/upload-chunk in 5 MB chunks,
 * then resolves with `{uploadId, filename}` so the caller can run /load.
 *
 * Auth: Bearer token attached automatically (Supabase session via core/auth).
 */
import { useCallback, useState } from 'react';

import i18n from 'i18next';

import { getApiUrl } from '@/config/environment';
import { getAuthToken } from '@/core/auth/supabase-auth';
import { log } from '@/core/debug/anomalyLogger';

const CHUNK_SIZE = 5 * 1024 * 1024;  // 5 MB

export interface AnomalyUploadResult {
  uploadId: string;
  filename: string;
}

export function useAnomalyUpload() {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const upload = useCallback(async (file: File): Promise<AnomalyUploadResult | null> => {
    setUploading(true);
    setError(null);
    setProgress(0);
    const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('Not authenticated');

      const uploadId = crypto.randomUUID();
      log.hook('useAnomalyUpload', 'upload starting', {
        fileName: file.name,
        sizeKB: Math.round(file.size / 1024),
        totalChunks,
        uploadId,
      });

      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const blob = file.slice(start, end);

        const fd = new FormData();
        fd.append('uploadId', uploadId);
        fd.append('chunkIndex', String(i));
        fd.append('totalChunks', String(totalChunks));
        fd.append('filename', file.name);
        fd.append('files[]', blob, file.name);

        log.net('→ POST chunk ' + i + '/' + totalChunks);
        const res = await fetch(getApiUrl('/api/adjustmentsOfData/upload-chunk'), {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        if (!res.ok) {
          const body = await res.text();
          throw new Error(`Chunk ${i} failed: ${res.status} ${body}`);
        }

        const progress = Math.round(((i + 1) / totalChunks) * 100);
        setProgress(progress);
        log.hook('useAnomalyUpload', 'chunk ok', { i, totalChunks, progress });
      }

      log.hook('useAnomalyUpload', 'upload complete', { uploadId, filename: file.name });
      return { uploadId, filename: file.name };
    } catch (e) {
      log.err('useAnomalyUpload.upload', e);
      const chunkMatch = /Chunk (\d+) failed/.exec((e as Error)?.message ?? '');
      const chunkNum = chunkMatch ? chunkMatch[1] : '?';
      const friendly = i18n.t('dataProcessing:anomaly.errors.uploadFailed', {
        chunk: chunkNum,
        total: totalChunks,
        defaultValue: `Upload failed at chunk ${chunkNum} of ${totalChunks}. Please retry.`,
      });
      setError(friendly);
      return null;
    } finally {
      setUploading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setUploading(false);
    setProgress(0);
    setError(null);
  }, []);

  return { upload, reset, uploading, progress, error };
}
